import { inject, Injectable } from '@angular/core';
import { environment } from '@fantaz/shared/app-config';
import { filter, Observable } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { AuthServiceInjectionToken } from './auth';

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  #authService = inject(AuthServiceInjectionToken);
  subject$: WebSocketSubject<unknown> | undefined;
  private fingerprintId: number | undefined;
  private url: string;
  connectClosed = false;
  private reconnectionSent = false;
  private toSend: unknown[] = [];

  /**
   * Establishes a WebSocket connection and returns a WebSocketSubject.
   * If a connection is already established and not closed, it returns the existing WebSocketSubject.
   * The connection is authenticated with the user token obtained from local storage.
   * @param {string} endpoint - Optional endpoint URL of the WebSocket.
   * @returns {WebSocketSubject<unknown>} - The WebSocketSubject for the established connection.
   */

  public connect(endpoint?: string): WebSocketSubject<unknown> {
    if (this.subject$ && !this.connectClosed) {
      return this.subject$;
    }
    const id_token = this.#authService.getUserIdToken();
    const websocketUrl = endpoint || environment.webSocketURL;
    this.url =
      websocketUrl +
      '?Authorization=' +
      id_token +
      '&fps=' +
      this.getFingerprint();
    this.subject$ = webSocket({
      url: this.url,
      closeObserver: {
        next: () => {
          // Handle WebSocket connection closed here
          console.log('[WebsocketService]: connection closed');
          this.connectClosed = true;
          this.subject$ = undefined;
        },
      },
      openObserver: {
        next: () => {
          console.log('[WebsocketService]: connection opened');
          this.connectClosed = false;
          if (this.toSend.length > 0) {
            this.toSend.forEach((message) => {
              this.sendMessage(message, true);
            });
            this.toSend = [];
            this.reconnectionSent = false;
          }
        },
      },
    });
    this.subject$.subscribe();
    return this.subject$;
  }

  sendMessage(message: unknown, retry = false): void {
    if (this.subject$ && !this.connectClosed) {
      this.subject$.next(message);
    } else {
      if (!retry) {
        this.toSend.push(message);
        if (!this.reconnectionSent) {
          this.connect();
          this.reconnectionSent = true;
        }
      }
    }
  }

  on(): WebSocketSubject<unknown> | undefined {
    return this.subject$;
  }

  onAction(
    action: string,
  ): Observable<{ action: string; body: any }> | undefined {
    return this.subject$?.pipe(
      filter((message: any) => message.action === action),
    );
  }

  close() {
    this.subject$?.complete();
  }

  // https://github.com/darkskyapp/string-hash
  checksum(str: string) {
    let hash = 5381,
      i = str.length;

    while (i--) hash = (hash * 33) ^ str.charCodeAt(i);

    return hash >>> 0;
  }

  getFingerprint() {
    if (!this.fingerprintId) {
      this.fingerprintId = this.checksum(
        [
          navigator.userAgent,
          [screen.height, screen.width, screen.colorDepth].join('x'),
          new Date().getTimezoneOffset(),
          !!window.sessionStorage,
          !!window.localStorage,
          !!document.cookie,
        ].join('###'),
      );
    }

    return this.fingerprintId;
  }
}
